import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Loader2,
  CalendarPlus,
  Search,
  History,
  Store,
  MapPin,
  Phone,
  Mail,
  Clock,
  AlertCircle,
  Wrench,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerPortal() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shopInfo, setShopInfo] = useState(null);
  const [error, setError] = useState(null);

  // Get subdomain from URL or query param
  const getSubdomain = () => {
    const querySubdomain = searchParams.get("shop");
    if (querySubdomain) return querySubdomain;

    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    if (parts.length >= 3 && parts[0] !== "www") {
      return parts[0];
    }
    return null;
  };

  const subdomain = getSubdomain();

  useEffect(() => {
    if (subdomain) {
      fetchShopInfo();
    } else {
      setError("No shop specified. Please use a valid shop link.");
      setLoading(false);
    }
  }, [subdomain]);

  const fetchShopInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/public/shop/${subdomain}/info`);
      setShopInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch shop info:", err);
      setError("Shop not found. Please check the link.");
    } finally {
      setLoading(false);
    }
  };

  const navigateWithShop = (path) => {
    // Preserve shop query param if present (for testing)
    const shopParam = searchParams.get("shop");
    if (shopParam) {
      navigate(`${path}?shop=${shopParam}`);
    } else {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Shop Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100" data-testid="customer-portal-home">
      {/* Hero Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          {shopInfo?.logo_url ? (
            <img
              src={shopInfo.logo_url.startsWith("http") ? shopInfo.logo_url : `${API.replace("/api", "")}${shopInfo.logo_url}`}
              alt={shopInfo.shop_name}
              className="w-20 h-20 object-contain mx-auto mb-4 rounded-xl"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-primary" />
            </div>
          )}
          <h1 className="text-3xl font-bold mb-2">{shopInfo?.shop_name}</h1>
          {shopInfo?.address && (
            <p className="text-muted-foreground flex items-center justify-center gap-2">
              <MapPin className="w-4 h-4" />
              {shopInfo.address}
            </p>
          )}
          <div className="flex items-center justify-center gap-6 mt-4 text-sm text-muted-foreground">
            {shopInfo?.phone && (
              <a href={`tel:${shopInfo.phone}`} className="flex items-center gap-1 hover:text-primary">
                <Phone className="w-4 h-4" />
                {shopInfo.phone}
              </a>
            )}
            {shopInfo?.email && (
              <a href={`mailto:${shopInfo.email}`} className="flex items-center gap-1 hover:text-primary">
                <Mail className="w-4 h-4" />
                {shopInfo.email}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Main Actions */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-xl font-semibold text-center mb-6">How can we help you?</h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {/* Book Repair */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
              shopInfo?.is_booking_enabled 
                ? "hover:border-primary" 
                : "opacity-60 cursor-not-allowed"
            }`}
            onClick={() => shopInfo?.is_booking_enabled && navigateWithShop("/book")}
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CalendarPlus className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Book a Repair</h3>
              <p className="text-sm text-muted-foreground">
                {shopInfo?.is_booking_enabled 
                  ? "Schedule an appointment for your device repair"
                  : "Online booking is currently unavailable"
                }
              </p>
              {shopInfo?.is_booking_enabled && (
                <Button className="mt-4 w-full" data-testid="book-repair-btn">
                  Book Now
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Track Repair */}
          <Card 
            className="cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] hover:border-blue-500"
            onClick={() => navigateWithShop("/track")}
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Track Repair</h3>
              <p className="text-sm text-muted-foreground">
                Check the status of your ongoing repair
              </p>
              <Button variant="outline" className="mt-4 w-full" data-testid="track-repair-btn">
                Track Status
              </Button>
            </CardContent>
          </Card>

          {/* Repair History */}
          <Card 
            className="cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] hover:border-purple-500"
            onClick={() => navigateWithShop("/history")}
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                <History className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Repair History</h3>
              <p className="text-sm text-muted-foreground">
                View all your past repairs at our shop
              </p>
              <Button variant="outline" className="mt-4 w-full" data-testid="history-btn">
                View History
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Shop Info Footer */}
        {shopInfo?.booking_instructions && (
          <Card className="mt-8 bg-blue-50 border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <Wrench className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Note from {shopInfo.shop_name}</p>
                  <p className="text-sm text-blue-700">{shopInfo.booking_instructions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-sm text-muted-foreground">
        <p>Powered by AfterSales.pro</p>
      </footer>
    </div>
  );
}
